<template>
  <v-container fluid>
    <v-card>
      <v-card-title>Developer Tools</v-card-title>
      <v-card-text>
        <!-- Autocomplete Profiles Migration Section -->
        <v-row>
          <v-col cols="12">
            <v-subheader class="px-0 text-h6">Sync Autocomplete Profiles</v-subheader>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="4">
            <v-btn
              color="primary"
              block
              :loading="loading.crm"
              :disabled="loading.crm"
              @click="syncCRMProfiles"
            >
              <v-icon left>mdi-sync</v-icon>
              Sync CRM Profiles
            </v-btn>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn
              color="primary"
              block
              :loading="loading.lpms"
              :disabled="loading.lpms"
              @click="syncLPMSProfiles"
            >
              <v-icon left>mdi-sync</v-icon>
              Sync LPMS Profiles
            </v-btn>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn
              color="primary"
              block
              :loading="loading.yomt"
              :disabled="loading.yomt"
              @click="syncYOMTProfiles"
            >
              <v-icon left>mdi-sync</v-icon>
              Sync YOMT Profiles
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Attachment Processor Section -->
        <v-row>
          <v-col cols="12">
            <v-subheader class="px-0 text-h6">Attachment Processor - CRM</v-subheader>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6">
            <v-btn
              color="info"
              block
              :loading="loading.findUnhandledCRM"
              :disabled="loading.findUnhandledCRM"
              @click="findUnhandledCRMAttachments"
            >
              <v-icon left>mdi-file-search</v-icon>
              Find Unhandled CRM Attachments
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn
              color="warning"
              block
              :loading="loading.processAllCRM"
              :disabled="loading.processAllCRM"
              @click="openProcessAllCRMDialog"
            >
              <v-icon left>mdi-cog</v-icon>
              Process All CRM Attachments
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12">
            <v-subheader class="px-0 text-h6">Attachment Processor - YOMT</v-subheader>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6">
            <v-btn
              color="info"
              block
              :loading="loading.findUnhandledYOMT"
              :disabled="loading.findUnhandledYOMT"
              @click="findUnhandledYOMTAttachments"
            >
              <v-icon left>mdi-file-search</v-icon>
              Find Unhandled YOMT Attachments
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn
              color="warning"
              block
              :loading="loading.processAllYOMT"
              :disabled="loading.processAllYOMT"
              @click="openProcessAllYOMTDialog"
            >
              <v-icon left>mdi-cog</v-icon>
              Process All YOMT Attachments
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12">
            <v-subheader class="px-0 text-h6">Attachment Processor - Stats</v-subheader>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6">
            <v-btn
              color="success"
              block
              :loading="loading.stats"
              :disabled="loading.stats"
              @click="checkAttachmentProcessorStats"
            >
              <v-icon left>mdi-chart-bar</v-icon>
              Check Processor Stats
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Results Section -->
        <v-row v-if="result">
          <v-col cols="12">
            <v-alert :type="resultType" dismissible @input="result = null">
              <div class="text-subtitle-2 mb-2">Result:</div>
              <pre class="result-content">{{ result }}</pre>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Process All CRM Dialog -->
    <v-dialog v-model="processAllCRMDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">Process All CRM Attachments</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="processLimitCRM"
                  label="Limit"
                  type="number"
                  outlined
                  dense
                  hint="Number of attachments to process"
                  persistent-hint
                  :min="1"
                  :max="1000"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loading.processAllCRM" @click="closeProcessAllCRMDialog">
            取消
          </v-btn>
          <v-btn
            color="warning"
            :loading="loading.processAllCRM"
            :disabled="loading.processAllCRM || !processLimitCRM"
            @click="processAllCRMAttachments"
          >
            立即執行
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Process All YOMT Dialog -->
    <v-dialog v-model="processAllYOMTDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">Process All YOMT Attachments</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="processLimitYOMT"
                  label="Limit"
                  type="number"
                  outlined
                  dense
                  hint="Number of attachments to process"
                  persistent-hint
                  :min="1"
                  :max="1000"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loading.processAllYOMT" @click="closeProcessAllYOMTDialog">
            取消
          </v-btn>
          <v-btn
            color="warning"
            :loading="loading.processAllYOMT"
            :disabled="loading.processAllYOMT || !processLimitYOMT"
            @click="processAllYOMTAttachments"
          >
            立即執行
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '@/api';
import ApiError from '@/api/ApiError';

export default {
  name: 'Developer',
  components: {},
  data() {
    return {
      loading: {
        crm: false,
        lpms: false,
        yomt: false,
        findUnhandledCRM: false,
        processAllCRM: false,
        findUnhandledYOMT: false,
        processAllYOMT: false,
        stats: false,
      },
      result: null,
      resultType: 'success',
      processAllCRMDialog: false,
      processLimitCRM: 50,
      processAllYOMTDialog: false,
      processLimitYOMT: 50,
    };
  },
  computed: {
    ...mapGetters('auth', ['token']),
  },
  methods: {
    async syncCRMProfiles() {
      const confirmed = await this.$root.$confirm.open(
        '確認同步',
        '確定要同步 CRM Autocomplete Profiles 嗎？',
        {
          color: 'primary',
        },
      );
      if (!confirmed) return;

      this.loading.crm = true;
      this.result = null;
      try {
        const response = await api.post('/v1/crm/migrate-autocomplete-profiles');
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'success';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'CRM Profiles 同步成功',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.crm = false;
      }
    },
    async syncLPMSProfiles() {
      const confirmed = await this.$root.$confirm.open(
        '確認同步',
        '確定要同步 LPMS Autocomplete Profiles 嗎？',
        {
          color: 'primary',
        },
      );
      if (!confirmed) return;

      this.loading.lpms = true;
      this.result = null;
      try {
        const response = await api.post('/v1/lpms/migrate-autocomplete-profiles');
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'success';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'LPMS Profiles 同步成功',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.lpms = false;
      }
    },
    async syncYOMTProfiles() {
      const confirmed = await this.$root.$confirm.open(
        '確認同步',
        '確定要同步 YOMT Autocomplete Profiles 嗎？',
        {
          color: 'primary',
        },
      );
      if (!confirmed) return;

      this.loading.yomt = true;
      this.result = null;
      try {
        const response = await api.post('/v1/yomt/migrate-autocomplete-profiles');
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'success';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'YOMT Profiles 同步成功',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.yomt = false;
      }
    },
    async findUnhandledCRMAttachments() {
      const confirmed = await this.$root.$confirm.open(
        '確認查詢',
        '確定要查詢未處理的 CRM 附件嗎？',
        {
          color: 'info',
        },
      );
      if (!confirmed) return;

      this.loading.findUnhandledCRM = true;
      this.result = null;
      try {
        const response = await api.get('/v1/attachment-processor/find-unhandled/crm');
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'info';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '查詢完成',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.findUnhandledCRM = false;
      }
    },
    openProcessAllCRMDialog() {
      this.processAllCRMDialog = true;
    },
    closeProcessAllCRMDialog() {
      this.processAllCRMDialog = false;
    },
    async processAllCRMAttachments() {
      const confirmed = await this.$root.$confirm.open(
        '確認處理',
        `確定要處理 ${this.processLimitCRM} 個 CRM 附件嗎？此操作可能需要一些時間。`,
        {
          color: 'warning',
        },
      );
      if (!confirmed) return;

      this.loading.processAllCRM = true;
      this.result = null;
      try {
        const response = await api.post('/v1/attachment-processor/process-all-individually/crm', {
          limit: this.processLimitCRM,
        });
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'success';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'CRM 附件處理完成',
          type: 'success',
          color: 'success',
        });
        this.closeProcessAllCRMDialog();
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.processAllCRM = false;
      }
    },
    async findUnhandledYOMTAttachments() {
      const confirmed = await this.$root.$confirm.open(
        '確認查詢',
        '確定要查詢未處理的 YOMT 附件嗎？',
        {
          color: 'info',
        },
      );
      if (!confirmed) return;

      this.loading.findUnhandledYOMT = true;
      this.result = null;
      try {
        const response = await api.get('/v1/attachment-processor/find-unhandled/yomt');
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'info';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '查詢完成',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.findUnhandledYOMT = false;
      }
    },
    openProcessAllYOMTDialog() {
      this.processAllYOMTDialog = true;
    },
    closeProcessAllYOMTDialog() {
      this.processAllYOMTDialog = false;
    },
    async processAllYOMTAttachments() {
      const confirmed = await this.$root.$confirm.open(
        '確認處理',
        `確定要處理 ${this.processLimitYOMT} 個 YOMT 附件嗎？此操作可能需要一些時間。`,
        {
          color: 'warning',
        },
      );
      if (!confirmed) return;

      this.loading.processAllYOMT = true;
      this.result = null;
      try {
        const response = await api.post('/v1/attachment-processor/process-all-individually/yomt', {
          limit: this.processLimitYOMT,
        });
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'success';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'YOMT 附件處理完成',
          type: 'success',
          color: 'success',
        });
        this.closeProcessAllYOMTDialog();
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.processAllYOMT = false;
      }
    },
    async checkAttachmentProcessorStats() {
      this.loading.stats = true;
      this.result = null;
      try {
        const response = await api.get('/v1/attachment-processor/stats');
        this.result = JSON.stringify(response.data, null, 2);
        this.resultType = 'success';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '統計資料已載入',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        this.result = ae.message;
        this.resultType = 'error';
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading.stats = false;
      }
    },
  },
};
</script>

<style scoped>
.result-content {
  max-height: 400px;
  overflow: auto;
  background-color: rgb(68 68 68);
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
